"use client";

import { useInfront } from "@/context/infront-context";
import { createInstrumentSlug } from "@/services/infront/utils";
import { createImageSizes } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import Clocks from "./clocks";

export const INDICIES = [
  {
    country: "Sverige",
    displayName: "OMXS30",
    name: "stockholm",
    flag: "se",
    instrument: { feed: 17921, ticker: "OMXS30" },
  },
  {
    country: "USA",
    displayName: "Nasdaq Composite",
    shortName: "NDX",
    name: "nasdaq",
    flag: "us",
    instrument: { feed: 2087, ticker: "COMP" },
  },
  {
    country: "Hongkong",
    displayName: "Hang Seng",
    name: "hongkong",
    flag: "hk",
    instrument: { feed: 2198, ticker: "HSI" },
  },
  {
    country: "Japan",
    displayName: "Nikkei",
    name: "tokyo",
    flag: "jp",
    instrument: { feed: 6150, ticker: "N225" },
  },
  {
    country: "Tyskland",
    displayName: "BDE40P",
    name: "frankfurt",
    flag: "de",
    instrument: { feed: 2302, ticker: "BDE40P" },
  },
];

const MapItem = (props: {
  index: {
    country: string;
    displayName: string;
    shortName?: string;
    name: string;
    flag: string;
    instrument: { feed: number; ticker: string };
  };
}) => {
  const router = useRouter();

  return (
    <div
      onClick={() =>
        router.push(
          createInstrumentSlug(
            props.index.instrument.feed,
            props.index.instrument.ticker,
          ),
        )
      }
      className={classNames(
        "cursor-pointer group absolute bg-white-500 p-2 rounded-sm flex items-center text-sm",
        {
          "top-[12%] left-[38%] lg:top-[20%] lg:left-[50%]":
            props.index.name === "stockholm",
          "top-[30%] left-[5%] lg:top-[55%]": props.index.name === "nasdaq",
          "top-[45%] right-[2%]": props.index.name === "tokyo",
          "top-[60%] right-[2%]": props.index.name === "hongkong",
          "top-[26%] left-[43%] lg:top-[37%] lg:left-[43%]":
            props.index.name === "frankfurt",
        },
      )}
    >
      <div className="relative w-[15px] h-[15px] rounded-full overflow-hidden">
        <Image
          fill
          alt={props.index.country}
          src={`/images/${props.index.flag}.svg`}
          className="object-cover"
          sizes={createImageSizes({
            mobile: "33vw",
            tablet: "33vw",
            desktop: "33vw",
          })}
        />
      </div>

      <span className="ml-1 font-bold mr-2 group-hover:underline">
        <span className="lg:hidden">{props.index.shortName ?? props.index.displayName}</span>
        <span className="hidden lg:inline">{props.index.displayName}</span>
      </span>

      <div
        id={`pct${props.index.instrument.ticker}`}
        className="font-semibold"
      />
    </div>
  );
};

const WordlMap = () => {
  const { isReady, load } = useInfront();

  const widgetsRef = useRef<any[]>([]);

  useEffect(() => {
    let active = true;

    load?.(["core", "highstock", "hollowcandlestick", "heikinashi"]).then(
      () => {
        if (!isReady || !active) return;

        widgetsRef.current.forEach((w) => w.destroy());
        widgetsRef.current = [];

        function setupMapWithSingleValues(instrument: {
          feed: number;
          ticker: string;
        }) {
          const widgetOptions =
            new window.Infront.InstrumentSingleValueWidgetOptions();

          widgetOptions.name = "PCT_CHANGE";
          widgetOptions.rootElement = "span";
          widgetOptions.enableChangeStatusColors = true;
          widgetOptions.instrument = instrument;

          const widget = window.infront.instrumentSingleValueWidget(
            `#pct${instrument.ticker}`,
            widgetOptions,
          );

          widgetsRef.current.push(widget);
        }

        INDICIES.forEach((index) => {
          setupMapWithSingleValues(index.instrument);
        });
      },
    );

    return () => {
      active = false;
      widgetsRef.current.forEach((w) => w.destroy());
      widgetsRef.current = [];
    };
  }, [isReady]);

  return (
    <div className="card !p-0">
      <div className="flex flex-col md:flex-row md:items-center justify-between px-4 py-4">
        <div className="font-noto-sans font-bold text-black text-lg normal-case">
          Översikt
        </div>

        <Clocks />
      </div>

      <div className="rounded-sm bg-cover bg-no-repeat bg-[center_10px] relative h-[330px] w-full bg-[url('/images/small-world-map.svg')] md:bg-[url('/images/world-map.svg')] market-map">
        <div id="map-items">
          {INDICIES.map((index) => (
            <MapItem index={index} key={index.displayName} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WordlMap;
